import React, {useState, useEffect, useContext, useMemo, useCallback, useRef} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Context} from '../index';
import {
    TextField,
    Button,
    Paper,
    Box,
    Checkbox,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions,
  } from '@mui/material';
import {Delete, Add, Remove} from '@mui/icons-material';
import {MaterialReactTable} from "material-react-table";
import MyButton from '../UI/MyButton/MyButton';
import AutocompleteField from '../components/AutocompleteField';
import BarcodeInput from '../components/BarcodeInput';
import PurchaseOrderSelector from '../components/PurchaseOrderSelector';
import GroupedReceiptItems from '../components/GroupedReceiptItems';
import NotificationModal from '../components/NotificationModal';
import ReceiptService from '../services/ReceiptService';
import { formatPrice, formatDiscount, getDiscountColors } from '../utils/currencyUtils';
import ConsignmentConverter from '../utils/ConsignmentConverter';
import {useTranslation} from 'react-i18next';

export default function CreateReceipt() {
    const {store} = useContext(Context);
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {id} = useParams();
    const isEdit = Boolean(id);

    
    // Состояние приемки
    const [receipt, setReceipt] = useState({
        store: null, // Будет установлен после загрузки складов
        receiptDate: new Date().toISOString().split('T')[0],
        items: [],
        status: 'draft'
    });

    // Дополнительное состояние для работы с заказами поставщиков
    const [counterparties, setCounterparties] = useState([]); // Список контрагентов в приемке
    const [storesLoading, setStoresLoading] = useState(true); // Загрузка складов
    const [storesLoadError, setStoresLoadError] = useState(false); // Ошибка загрузки складов

    // Состояния UI
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [scannerActive, setScannerActive] = useState(true);
    const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
    const [currentScannedItem, setCurrentScannedItem] = useState(null);
    const [tempQuantity, setTempQuantity] = useState('1');
    const [validationDialog, setValidationDialog] = useState({open: false, message: '', items: []});
    const [noteDialog, setNoteDialog] = useState({open: false, itemUniqueId: null, itemName: '', currentNote: ''});
    const [tempNote, setTempNote] = useState('');

    // // Диалог для подтверждения дубликата товара
    // const [duplicateDialog, setDuplicateDialog] = useState({
    //     open: false,
    //     productName: '',
    //     existingOrders: '',
    //     availableOrder: null,
    //     onConfirm: null
    // });

    // Новые состояния для работы с заказами поставщиков
    const [purchaseOrderSelectorOpen, setPurchaseOrderSelectorOpen] = useState(false);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [currentProductForSelection, setCurrentProductForSelection] = useState(null);

    // Состояние для управления уведомлениями
    const [notification, setNotification] = useState({
        open: false,
        type: 'success',
        title: '',
        message: '',
        onRetry: null,
        shouldNavigate: false
    });

    // Реф для BarcodeInput компонента
    const barcodeInputRef = useRef(null);

    // Функции для показа уведомлений
    const showSuccess = useCallback((message, title = null, shouldNavigate = false) => {
        setNotification({
            open: true,
            type: 'success',
            title,
            message,
            onRetry: null,
            shouldNavigate
        });
    }, []);

    const showError = useCallback((message, onRetry = null, title = null) => {
        setNotification({
            open: true,
            type: 'error',
            title,
            message,
            onRetry,
            shouldNavigate: false // Для ошибок навигация не нужна
        });
    }, []);

    const closeNotification = useCallback(() => {
        setNotification(prev => {
            const shouldNavigate = prev.type === 'success' && prev.shouldNavigate;
            // Сразу закрываем модальное окно
            const newState = { ...prev, open: false };

            // Если нужно навигировать - делаем это после закрытия
            if (shouldNavigate) {
                setTimeout(() => navigate('/receipts'), 100);
            }

            return newState;
        });
    }, [navigate]);

    // Функция генерации стабильного ID для товара (для существующих товаров)
    const generateStableUniqueId = useCallback((barcode, purchaseOrderId, boxNumber) => {
        const key = `${barcode}_${purchaseOrderId || 'no_order'}_${boxNumber || 1}`;
        // Простая детерминированная хэш-функция
        let hash = 0;
        for (let i = 0; i < key.length; i++) {
            const char = key.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(16).padStart(8, '0');
    }, []);

    // Функция генерации уникального ID для товара (для новых товаров)
    const generateUniqueId = useCallback(() => {
        return crypto.randomUUID();
    }, []);

    // Функции-обработчики
    const handleQuantityChange = useCallback((uniqueId, newQuantity) => {
        const newItems = [...receipt.items];
        const itemIndex = newItems.findIndex(item => item.uniqueId === uniqueId);

        if (itemIndex !== -1) {
            const item = newItems[itemIndex];
            const oldQuantity = item.quantity;

            // Сохраняем исходное количество из заказа при первом добавлении товара
            if (!item.originalQuantity) {
                item.originalQuantity = oldQuantity;
            }

            // Обновляем количество
            item.quantity = newQuantity;

            // Управляем примечанием об изменении количества
            if (item.originalQuantity) {
                const quantityChangeNote = `Изменено количество: было ${item.originalQuantity}, стало ${newQuantity}`;
                const hasQuantityChangeNote = item.note && item.note.includes('Изменено количество:');

                if (newQuantity !== item.originalQuantity) {
                    // Количество изменено - добавляем или обновляем примечание
                    if (hasQuantityChangeNote) {
                        // Обновляем существующее примечание
                        const lines = item.note.split('\n');
                        const updatedLines = lines.map(line =>
                            line.startsWith('Изменено количество:')
                                ? quantityChangeNote
                                : line
                        );
                        item.note = updatedLines.join('\n');
                    } else {
                        // Добавляем новое примечание
                        if (item.note && item.note.trim()) {
                            item.note = `${item.note}\n${quantityChangeNote}`;
                        } else {
                            item.note = quantityChangeNote;
                        }
                    }
                } else {
                    // Количество возвращено к исходному - удаляем примечание об изменении
                    if (hasQuantityChangeNote) {
                        const lines = item.note.split('\n');
                        const filteredLines = lines.filter(line => !line.startsWith('Изменено количество:'));

                        if (filteredLines.length > 0 && filteredLines.some(line => line.trim())) {
                            item.note = filteredLines.join('\n');
                        } else {
                            item.note = null; // Удаляем примечание полностью, если ничего не осталось
                        }
                    }
                }
            }

            setReceipt(prev => ({...prev, items: newItems}));
        }
    }, [receipt.items]);

    // Функция для обновления списка контрагентов
    const updateCounterpartiesList = (items) => {
        const uniqueCounterparties = {};
        items.forEach(item => {
            if (item.agentId && item.agentName) {
                uniqueCounterparties[item.agentId] = {
                    id: item.agentId,
                    name: item.agentName
                };
            }
        });
        setCounterparties(Object.values(uniqueCounterparties));
    };

    // Функция для возврата фокуса на поле сканирования
    const focusBarcodeInput = useCallback(() => {
        setTimeout(() => {
            setScannerActive(true);
            // Проверяем, есть ли у BarcodeInput метод focus()
            if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === 'function') {
                barcodeInputRef.current.focus();
            }
        }, 500); // Небольшая задержка для завершения всех обновлений
    }, [setScannerActive]);

    const handleRemoveItem = useCallback((uniqueId) => {
        const removedItem = receipt.items.find(item => item.uniqueId === uniqueId);
        const newItems = receipt.items.filter(item => item.uniqueId !== uniqueId);

        // Перенумеровываем коробки того же товара в том же заказе
        let renumbered = newItems;
        if (removedItem) {
            let boxCounter = 0;
            renumbered = newItems.map(item => {
                if (item.barcode === removedItem.barcode &&
                    item.purchaseOrderId === removedItem.purchaseOrderId) {
                    boxCounter++;
                    return { ...item, boxNumber: boxCounter };
                }
                return item;
            });
        }

        setReceipt(prev => ({...prev, items: renumbered}));

        // Обновляем список контрагентов после удаления товара
        updateCounterpartiesList(renumbered);
    }, [receipt.items, updateCounterpartiesList]);

    const handleOpenNoteDialog = useCallback((uniqueId, itemName, currentNote) => {
        setNoteDialog({
            open: true,
            itemUniqueId: uniqueId,
            itemName,
            currentNote: currentNote || ''
        });
        setTempNote(currentNote || '');
    }, []);

    // Table columns for items
    const columns = useMemo(
        () => [
            {
                accessorKey: 'miniature',
                header: t('label.photo'),
                size: 144,
                Cell: ({ cell }) => (
                    <Box display="flex" justifyContent="center">
                        {cell.getValue() ? (
                            <img
                                src={cell.getValue()}
                                alt="Product"
                                style={{
                                    width: '96px',
                                    height: '96px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '96px',
                                    height: '96px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: '#999'
                                }}
                            >
                                {t('msg.noPhoto')}
                            </Box>
                        )}
                    </Box>
                )
            },
            {
                accessorKey: 'barcode',
                header: t('label.barcode'),
                size: 150
            },
            {
                accessorKey: 'name',
                header: t('label.name'),
                size: 250,
                Cell: ({ row }) => {
                    const item = row.original;
                    const hasConsignment = item.consignmentId && item.consignmentLabel;

                    return (
                        <div>
                            <div>{item.name}</div>
                            {hasConsignment && (
                                <div style={{
                                    display: 'inline-block',
                                    marginTop: '4px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    color: '#1565c0',
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #90caf9',
                                    borderRadius: '4px'
                                }}>
                                    {t('receipts.series', {label: item.consignmentLabel})}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'article',
                header: t('label.article'),
                size: 120
            },
            {
                accessorKey: 'price',
                header: t('label.price'),
                size: 120,
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                            {formatPrice(item.price, item.currency)}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'discount',
                header: t('label.discount'),
                size: 100,
                Cell: ({ row }) => {
                    const item = row.original;
                    const discountColors = getDiscountColors(item.discount);

                    return (
                        <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: discountColors.color,
                            textAlign: 'left',
                        }}>
                            {formatDiscount(item.discount)}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'purchaseOrderName',
                header: t('receipts.purchaseOrder'),
                size: 200,
                Cell: ({ row }) => {
                    const orderName = row.original.purchaseOrderName;
                    return (
                        <div style={{ fontSize: '12px' }}>
                            {orderName || '—'}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'quantity',
                header: t('label.quantity'),
                size: 120,
                Cell: ({ row, cell }) => {
                    const value = cell.getValue();
                    const index = row.index;
                    const isQuantityChanged = row.original.originalQuantity && value !== row.original.originalQuantity;

                    return (
                        <TextField
                            type="number"
                            value={value}
                            onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue >= 0) {
                                    handleQuantityChange(row.original.uniqueId, newValue);
                                }
                            }}
                            inputProps={{
                                min: 0,
                                step: 1,
                                style: {
                                    textAlign: 'center',
                                    fontWeight: isQuantityChanged ? 'bold' : 'normal',
                                    color: isQuantityChanged ? '#f24405' : 'inherit',
                                    backgroundColor: isQuantityChanged ? '#fff3e0' : 'transparent'
                                }
                            }}
                            size="small"
                            sx={{
                                width: '80px',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: isQuantityChanged ? '#ff9800' : '#ddd',
                                        borderWidth: isQuantityChanged ? '2px' : '1px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isQuantityChanged ? '#f57c00' : '#bbb'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#f24405',
                                        borderWidth: '2px'
                                    }
                                }
                            }}
                        />
                    );
                }
            },
            {
                accessorKey: 'note',
                header: t('label.note'),
                size: 120,
                Cell: ({ cell, row }) => {
                    const noteValue = cell.getValue();
                    const hasNote = noteValue !== null && noteValue !== undefined;
                    const isEmpty = noteValue === null || noteValue === undefined || noteValue === '';

                    return (
                        <div
                            onClick={() => handleOpenNoteDialog(row.original.uniqueId, row.original.name, noteValue)}
                            style={{
                                cursor: 'pointer',
                                padding: '5px',
                                borderRadius: '4px',
                                backgroundColor: hasNote ? '#ffebee' : 'transparent',
                                color: hasNote ? '#d32f2f' : '#666',
                                fontSize: '12px',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                            }}
                        >
                            {hasNote && <span>🔴</span>}
                            {!hasNote ? '—' : (noteValue === '' ? t('receipts.marked') : t('receipts.hasNote'))}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'actions',
                header: t('label.actions'),
                size: 80,
                enableSorting: false,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={() => handleRemoveItem(row.original.uniqueId)}
                        color="error"
                        size="small"
                    >
                        <Delete />
                    </IconButton>
                )
            }
        ],
        [handleQuantityChange, handleOpenNoteDialog, handleRemoveItem, t]
    );

    // Загрузка складов и приемки при редактировании
    useEffect(() => {
        // Загружаем склады для установки значения по умолчанию
        const loadDefaultStore = async () => {
            try {
                const response = await ReceiptService.searchStores('');
                const stores = response.data || [];

                // Если склады не загрузились или массив пуст - это ошибка
                if (!stores || stores.length === 0) {
                    throw new Error('Склады не найдены или не удалось загрузить');
                }

                // Ищем склад "Казань, склад А" или похожий
                let defaultStore = stores.find(store =>
                    store.name.toLowerCase().includes('казань') &&
                    store.name.toLowerCase().includes('склад а')
                );

                // Если не нашли, ищем любой склад в Казани
                if (!defaultStore) {
                    defaultStore = stores.find(store =>
                        store.name.toLowerCase().includes('казань')
                    );
                }

                // Если все еще не нашли, берем первый склад
                if (!defaultStore && stores.length > 0) {
                    defaultStore = stores[0];
                }

                if (defaultStore) {
                    setReceipt(prev => ({...prev, store: defaultStore}));
                    setStoresLoadError(false); // Успешная загрузка
                }
            } catch (error) {
                console.error('Error loading default store:', error);
                setStoresLoadError(true); // Устанавливаем флаг ошибки
            } finally {
                setStoresLoading(false);
            }
        };

        loadDefaultStore();

        if (isEdit && id) {
            loadReceipt();
        }
    }, [id, isEdit]);

    // Автосохранение каждые 30 секунд
    useEffect(() => {
        if (!isEdit || receipt.status !== 'draft') return;

        const autoSave = setInterval(() => {
            if (receipt.items.length > 0) {
                handleSaveDraft(true); // silent save
            }
        }, 30000);

        return () => clearInterval(autoSave);
    }, [receipt, isEdit]);

    // Автоматический возврат фокуса на поле сканирования при закрытии всех диалогов
    useEffect(() => {
        const anyDialogOpen =
            quantityDialogOpen ||
            noteDialog.open ||
            purchaseOrderSelectorOpen ||
            notification.open ||
            validationDialog.open;

        if (
            !anyDialogOpen &&
            scannerActive &&
            receipt.store &&
            !storesLoading &&
            !storesLoadError &&
            receipt.status === 'draft' &&
            barcodeInputRef.current
        ) {
            const timer = setTimeout(() => {
                if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === 'function') {
                    barcodeInputRef.current.focus();
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [
        quantityDialogOpen,
        noteDialog.open,
        purchaseOrderSelectorOpen,
        notification.open,
        validationDialog.open,
        scannerActive,
        receipt.store,
        storesLoading,
        storesLoadError,
        receipt.status
    ]);

    const loadReceipt = async () => {
        setLoading(true);
        try {
            const response = await ReceiptService.getReceiptById(id);
            const receiptData = response.data;

            // Обрабатываем товары: сначала расставляем boxNumber, потом uniqueId
            const processedItems = (receiptData.items || []).map(item => ({
                ...item,
                originalQuantity: item.originalQuantity || item.quantity
            }));

            // СНАЧАЛА: Расставить boxNumber для старых записей (без boxNumber)
            const boxCounters = {};
            processedItems.forEach(item => {
                const key = `${item.barcode}_${item.purchaseOrderId || 'no_order'}`;
                if (!boxCounters[key]) boxCounters[key] = 0;
                boxCounters[key]++;
                if (!item.boxNumber) {
                    item.boxNumber = boxCounters[key];
                }
            });

            // ПОТОМ: Генерируем uniqueId с уже правильным boxNumber
            processedItems.forEach(item => {
                if (!item.uniqueId) {
                    item.uniqueId = generateStableUniqueId(
                        item.barcode || `unknown_barcode`,
                        item.purchaseOrderId || 'no_order',
                        item.boxNumber
                    );
                }
            });

            setReceipt(prev => ({
                ...receiptData,
                items: processedItems,
                // Сохраняем склад из приемки, если он есть, иначе оставляем текущий (загруженный по умолчанию)
                store: receiptData.store || prev.store,
                receiptDate: receiptData.receiptDate ?
                    (new Date(receiptData.receiptDate).toISOString().split('T')[0]) :
                    new Date().toISOString().split('T')[0]
            }));

            // Обновляем список контрагентов из загруженных данных
            updateCounterpartiesList(processedItems);
        } catch (error) {
            console.error('Error loading receipt:', error);
            alert(t('msg.errorLoadingReceipt'));
            navigate('/receipts');
        } finally {
            setLoading(false);
        }
    };

    // Новая функция для загрузки приемки с указанным ID (для использования после отправки)
    const loadReceiptWithId = async (receiptId) => {
        if (!receiptId) {
            console.error('Cannot load receipt: no receiptId provided');
            return;
        }

        try {
            const response = await ReceiptService.getReceiptById(receiptId);
            const receiptData = response.data;

            // Обрабатываем товары: сначала расставляем boxNumber, потом uniqueId
            const processedItems = (receiptData.items || []).map(item => ({
                ...item,
                originalQuantity: item.originalQuantity || item.quantity
            }));

            // СНАЧАЛА: Расставить boxNumber для старых записей (без boxNumber)
            const boxCounters = {};
            processedItems.forEach(item => {
                const key = `${item.barcode}_${item.purchaseOrderId || 'no_order'}`;
                if (!boxCounters[key]) boxCounters[key] = 0;
                boxCounters[key]++;
                if (!item.boxNumber) {
                    item.boxNumber = boxCounters[key];
                }
            });

            // ПОТОМ: Генерируем uniqueId с уже правильным boxNumber
            processedItems.forEach(item => {
                if (!item.uniqueId) {
                    item.uniqueId = generateStableUniqueId(
                        item.barcode || `unknown_barcode`,
                        item.purchaseOrderId || 'no_order',
                        item.boxNumber
                    );
                }
            });

            setReceipt(prev => ({
                ...receiptData,
                items: processedItems,
                // Сохраняем склад из приемки, если он есть, иначе оставляем текущий
                store: receiptData.store || prev.store,
                receiptDate: receiptData.receiptDate ?
                    (new Date(receiptData.receiptDate).toISOString().split('T')[0]) :
                    new Date().toISOString().split('T')[0]
            }));

            // Обновляем список контрагентов из загруженных данных
            updateCounterpartiesList(processedItems);
        } catch (error) {
            console.error('Error loading receipt with ID:', error);
            // Не показываем алерт и не редиректим пользователя, т.к. это может быть фоновая операция
        }
    };

    // Расчет количества для новой коробки с учетом уже добавленных товаров
    const calcQuantity = useCallback((order, barcode, orderId) => {
        const alreadyInReceipt = receipt.items
            .filter(i => i.barcode === barcode && i.purchaseOrderId === orderId)
            .reduce((sum, i) => sum + i.quantity, 0);
        const trueRemaining = order.remainingQuantity - alreadyInReceipt;
        if (trueRemaining <= 0) return null;
        return order.quantityInBox
            ? Math.min(order.quantityInBox, trueRemaining)
            : trueRemaining;
    }, [receipt.items]);

    const handleBarcodeScanned = async (barcode) => {
        if (!scannerActive || !barcode.trim()) return;

        setScannerActive(false);

        try {
            // Поиск товара в заказах поставщиков
            const response = await ReceiptService.findProductInPurchaseOrders(barcode);
            const searchResult = response.data;


            if (!searchResult.found) {
                if (searchResult.reason === 'fully_received') {
                    showError(t('receipts.productFullyReceivedInOrders', {barcode}));
                } else {
                    showError(t('receipts.productBarcodeNotFound', {barcode}));
                }
                setTimeout(() => setScannerActive(true), 1000);
                return;
            }

            const allOrders = searchResult.orders;
            const isConsignment = allOrders[0].type === 'consignment';

            const product = {
                barcode: allOrders[0].barcode,
                productId: allOrders[0].productId,
                productType: allOrders[0].type,
                name: allOrders[0].productName,
                article: allOrders[0].article || '',
                pathName: allOrders[0].pathName || '',
                miniature: allOrders[0].miniature || '',
                note: null,
                // Поля серии (consignment) - заполняются если type === 'consignment'
                consignmentId: isConsignment ? allOrders[0].productId : null,
                consignmentName: isConsignment ? allOrders[0].productName : null,
                consignmentLabel: isConsignment ? allOrders[0].label : null,
                consignmentBarcode: isConsignment ? allOrders[0].barcode : null
            };

            // Проверяем, есть ли уже этот товар в приемке — авто-биндинг
            const existingItems = receipt.items.filter(item =>
                item.barcode === barcode && item.purchaseOrderId
            );

            if (existingItems.length > 0) {
                // Товар уже есть — создаём новую коробку для того же заказа
                const boundOrderId = existingItems[0].purchaseOrderId;
                const boundOrder = allOrders.find(o => o.purchaseOrderId === boundOrderId);

                if (boundOrder) {
                    const existingBoxCount = existingItems.filter(
                        i => i.purchaseOrderId === boundOrderId
                    ).length;
                    const quantity = calcQuantity(boundOrder, barcode, boundOrderId);

                    if (quantity === null) {
                        showError(t('receipts.productFullyAccepted', {name: product.name}));
                        setTimeout(() => setScannerActive(true), 1000);
                        return;
                    }

                    const newItem = {
                        barcode: product.barcode,
                        productId: product.productId,
                        productType: product.productType,
                        name: product.name,
                        article: product.article,
                        pathName: product.pathName,
                        miniature: product.miniature,
                        note: product.note,
                        consignmentId: product.consignmentId,
                        consignmentName: product.consignmentName,
                        consignmentLabel: product.consignmentLabel,
                        consignmentBarcode: product.consignmentBarcode,
                        uniqueId: generateUniqueId(),
                        purchaseOrderId: boundOrder.purchaseOrderId,
                        purchaseOrderName: boundOrder.purchaseOrderName,
                        agentId: boundOrder.agentId,
                        agentName: boundOrder.agentName,
                        storeId: boundOrder.storeId,
                        storeName: boundOrder.storeName,
                        price: boundOrder.price,
                        discount: boundOrder.discount,
                        currency: boundOrder.currency,
                        quantity: quantity,
                        originalQuantity: quantity,
                        boxNumber: existingBoxCount + 1
                    };

                    setReceipt(prev => ({
                        ...prev,
                        items: [...prev.items, newItem]
                    }));
                    updateCounterpartiesList([...receipt.items, newItem]);
                    focusBarcodeInput();
                    setTimeout(() => setScannerActive(true), 1000);
                    return;
                } else {
                    // bound заказ не найден — значит больше не доступен
                    showError(t('receipts.productBoundOrderUnavailable', {name: product.name, order: existingItems[0].purchaseOrderName || ''}));
                    setTimeout(() => setScannerActive(true), 1000);
                    return;
                }
            }

            // Нет существующего биндинга — определяем доступные заказы
            const availableOrders = allOrders;

            if (availableOrders.length === 0) {
                showError(t('receipts.productNotFoundInOrders', {name: product.name}));
                setTimeout(() => setScannerActive(true), 1000);
                return;
            }

            // Обработка доступных заказов
            if (availableOrders.length === 1) {
                // Один заказ — автоматически добавляем товар
                const selectedOrder = availableOrders[0];
                const quantity = calcQuantity(selectedOrder, barcode, selectedOrder.purchaseOrderId);

                if (quantity === null) {
                    showError(t('receipts.productFullyAcceptedForOrder', {name: product.name, order: selectedOrder.purchaseOrderName}));
                    setTimeout(() => setScannerActive(true), 1000);
                    return;
                }

                const newItem = {
                    barcode: product.barcode,
                    productId: product.productId,
                    productType: product.productType,
                    name: product.name,
                    article: product.article,
                    pathName: product.pathName,
                    miniature: product.miniature,
                    note: product.note,
                    consignmentId: product.consignmentId,
                    consignmentName: product.consignmentName,
                    consignmentLabel: product.consignmentLabel,
                    consignmentBarcode: product.consignmentBarcode,
                    uniqueId: generateUniqueId(),
                    purchaseOrderId: selectedOrder.purchaseOrderId,
                    purchaseOrderName: selectedOrder.purchaseOrderName,
                    agentId: selectedOrder.agentId,
                    agentName: selectedOrder.agentName,
                    storeId: selectedOrder.storeId,
                    storeName: selectedOrder.storeName,
                    price: selectedOrder.price,
                    discount: selectedOrder.discount,
                    currency: selectedOrder.currency,
                    quantity: quantity,
                    originalQuantity: quantity,
                    boxNumber: 1
                };

                setReceipt(prev => ({
                    ...prev,
                    items: [...prev.items, newItem]
                }));
                updateCounterpartiesList([...receipt.items, newItem]);
                focusBarcodeInput();

            } else {
                // Несколько заказов — открываем диалог выбора
                setCurrentProductForSelection({
                    ...product,
                    allOrders: allOrders
                });
                setAvailableOrders(availableOrders);
                setPurchaseOrderSelectorOpen(true);
            }

        } catch (error) {
            console.error('Error finding product:', error);
            showError(t('receipts.errorSearchingProduct', {barcode}));
        }

        // Return scanner activity after a short delay
        setTimeout(() => setScannerActive(true), 1000);
    };

    // Функция обработки выбора заказа поставщика
    const handlePurchaseOrderSelected = (selectedOrder) => {
        if (!currentProductForSelection || !selectedOrder) return;

        // Считаем существующие коробки для этого barcode+orderId
        const existingBoxCount = receipt.items.filter(
            item => item.barcode === currentProductForSelection.barcode &&
                    item.purchaseOrderId === selectedOrder.purchaseOrderId
        ).length;

        const quantity = calcQuantity(selectedOrder, currentProductForSelection.barcode, selectedOrder.purchaseOrderId);

        if (quantity === null) {
            showError(t('receipts.productFullyAcceptedForOrder', {name: currentProductForSelection.name, order: selectedOrder.purchaseOrderName}));
            setPurchaseOrderSelectorOpen(false);
            setCurrentProductForSelection(null);
            setAvailableOrders([]);
            return;
        }

        const newItem = {
            // Базовые данные о товаре из currentProductForSelection
            barcode: currentProductForSelection.barcode,
            productId: currentProductForSelection.productId,
            productType: currentProductForSelection.productType,
            name: currentProductForSelection.name,
            article: currentProductForSelection.article,
            pathName: currentProductForSelection.pathName,
            miniature: currentProductForSelection.miniature,
            note: currentProductForSelection.note,
            // Поля серии (consignment)
            consignmentId: currentProductForSelection.consignmentId,
            consignmentName: currentProductForSelection.consignmentName,
            consignmentLabel: currentProductForSelection.consignmentLabel,
            consignmentBarcode: currentProductForSelection.consignmentBarcode,
            // Данные из выбранного заказа
            uniqueId: generateUniqueId(),
            purchaseOrderId: selectedOrder.purchaseOrderId,
            purchaseOrderName: selectedOrder.purchaseOrderName,
            agentId: selectedOrder.agentId,
            agentName: selectedOrder.agentName,
            storeId: selectedOrder.storeId,
            storeName: selectedOrder.storeName,
            // Цена, скидка и валюта из заказа
            price: selectedOrder.price,
            discount: selectedOrder.discount,
            currency: selectedOrder.currency,
            // Количество: используем calcQuantity
            quantity: quantity,
            originalQuantity: quantity,
            boxNumber: existingBoxCount + 1
        };

        setReceipt(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));

        // Обновляем список контрагентов
        updateCounterpartiesList([...receipt.items, newItem]);

        // Возвращаем фокус на поле сканирования
        focusBarcodeInput();

        // Сбрасываем состояние выбора
        setCurrentProductForSelection(null);
        setAvailableOrders([]);
        setPurchaseOrderSelectorOpen(false);
    };

    const handleQuantityConfirmed = () => {
        if (currentScannedItem && tempQuantity && parseInt(tempQuantity) > 0) {
            const newItem = {
                ...currentScannedItem,
                uniqueId: generateUniqueId(currentScannedItem.barcode, currentScannedItem.purchaseOrderId || 'no_order'),
                quantity: parseInt(tempQuantity)
            };

            setReceipt(prev => ({
                ...prev,
                items: [...prev.items, newItem]
            }));

            // Обновляем список контрагентов
            updateCounterpartiesList([...receipt.items, newItem]);
        }

        setQuantityDialogOpen(false);
        setCurrentScannedItem(null);
        setTempQuantity('1');
    };

    const handleQuantityDialogKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleQuantityConfirmed();
        }
    };

    const handleNoteChange = (uniqueId, note) => {
        const newItems = [...receipt.items];
        const itemIndex = newItems.findIndex(item => item.uniqueId === uniqueId);

        if (itemIndex !== -1) {
            newItems[itemIndex] = { ...newItems[itemIndex], note };
            setReceipt(prev => ({...prev, items: newItems}));
        }
    };

    const handleCloseNoteDialog = () => {
        setNoteDialog({open: false, itemUniqueId: null, itemName: '', currentNote: ''});
        setTempNote('');
    };

    const handleSaveNote = () => {
        handleNoteChange(noteDialog.itemUniqueId, tempNote === '' ? '' : tempNote);
        handleCloseNoteDialog();
    };

    const handleRemoveNote = () => {
        handleNoteChange(noteDialog.itemUniqueId, null);
        handleCloseNoteDialog();
    };

    // // === СТАРЫЕ ФУНКЦИИ: Обработка диалога дубликата (ЗАКОММЕНТИРОВАНО) ===
    // const handleDuplicateConfirm = () => {
    //     if (duplicateDialog.onConfirm) {
    //         duplicateDialog.onConfirm();
    //     }
    //     setDuplicateDialog({open: false, productName: '', existingOrders: '', availableOrder: null, onConfirm: null});
    //     setTimeout(() => setScannerActive(true), 1000);
    // };

    // const handleDuplicateCancel = () => {
    //     setDuplicateDialog({open: false, productName: '', existingOrders: '', availableOrder: null, onConfirm: null});
    //     setTimeout(() => setScannerActive(true), 1000);
    // };

    const handleSaveDraft = async (silent = false) => {
        if (!receipt.store) {
            if (!silent) alert(t('receipts.pleaseSelectWarehouse'));
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await ReceiptService.updateReceipt(id, receipt);
            } else {
                const response = await ReceiptService.createReceipt(receipt);
                setReceipt(prev => ({...prev, _id: response.data._id}));
                // Update URL without redirect
                window.history.replaceState({}, '', `/receipts/${response.data._id}`);
            }

            if (!silent) alert(t('receipts.draftSaved'));
        } catch (error) {
            console.error('Error saving draft:', error);
            if (!silent) alert(t('receipts.errorSaving'));
        } finally {
            setSaving(false);
        }
    };

    const handleSendToMoysklad = async () => {
        // Validation
        if (!receipt.store) {
            alert(t('receipts.pleaseFillWarehouse'));
            return;
        }

        if (receipt.items.length === 0) {
            alert(t('receipts.pleaseAddItem'));
            return;
        }

        // Check that all items are linked to purchase orders
        const itemsWithoutPurchaseOrder = receipt.items.filter(item => !item.purchaseOrderId);
        if (itemsWithoutPurchaseOrder.length > 0) {
            alert(t('receipts.itemsNotLinked'));
            return;
        }

        await sendReceipt();
    };

    const handleSetDefaultQuantity = async () => {
        // Set quantity = 1 for all items without quantity
        const newItems = receipt.items.map(item => ({
            ...item,
            quantity: item.quantity > 0 ? item.quantity : 1
        }));

        setReceipt(prev => ({...prev, items: newItems}));
        setValidationDialog({open: false, message: '', items: []});

        await sendReceipt();
    };

    const sendReceipt = async () => {
        setSaving(true);
        try {
            let receiptId = id || receipt._id;

            // Check receipt status
            if (receipt.status === 'sent') {
                alert(t('receipts.receiptAlreadySent'));
                return;
            }

            // If no ID, create receipt first
            if (!receiptId) {
                const response = await ReceiptService.createReceipt(receipt);
                receiptId = response.data._id;
                setReceipt(prev => ({...prev, _id: receiptId}));
            } else {
                // Update receipt before sending
                await ReceiptService.updateReceipt(receiptId, receipt);
            }

            // Use new endpoint for splitting and sending
            const response = await ReceiptService.splitAndSendReceipt(receiptId);
            const result = response.data;

            // Show results
            let message = `${t('receipts.processingComplete')}\n\n`;
            message += `${t('receipts.totalOrders', {count: result.totalOrders})}\n`;
            message += `${t('receipts.successfullySent', {count: result.successCount})}\n`;
            message += `${t('receipts.errorsCount', {count: result.errorCount})}\n\n`;

            if (result.results.length > 0) {
                message += `${t('receipts.resultsByOrder')}\n`;
                result.results.forEach((res, index) => {
                    message += `\n${index + 1}. ${res.agentName}\n`;
                    message += `   Order: ${res.purchaseOrderName}\n`;
                    message += `   Items: ${res.itemsCount}\n`;
                    if (res.success) {
                        message += `   ${t('receipts.sentMoyskladId', {id: res.moyskladId})}\n`;
                    } else {
                        message += `   ${t('receipts.errorMessage', {error: res.error})}\n`;
                    }
                });
            }

            // Show modal with results
            // Determine result type
            const allSuccess = result.successCount === result.totalOrders && result.errorCount === 0;
            const hasSuccess = result.successCount > 0;

            if (allSuccess) {
                // Full success - all orders sent successfully
                showSuccess(message, t('receipts.sendingComplete'), true);
            } else if (hasSuccess) {
                // Partial success - some sent, some with errors
                message += `\n${t('receipts.someReceiptsWithErrors')}`;
                showError(message, () => sendReceipt(), t('receipts.partialSending'));
            } else {
                // Complete failure - all attempts with errors
                message += `\n${t('receipts.allAttemptsFailed')}`;
                showError(message, () => sendReceipt(), t('receipts.sendingErrorTitle'));
            }

        } catch (error) {
            console.error('Error sending receipt:', error);

            // Handle order validation errors (Scenario 4.4)
            if (error.response?.status === 400 && error.response?.data?.invalidItems) {
                const errorData = error.response.data;
                let message = errorData.message + '\n\n';

                if (errorData.invalidItems && errorData.invalidItems.length > 0) {
                    message += `${t('receipts.problemItems')}\n`;
                    errorData.invalidItems.forEach((item, index) => {
                        message += `\n${index + 1}. ${item.name} (article: ${item.barcode || 'not specified'})\n`;
                        message += `   Order: ${item.purchaseOrderName || 'ID: ' + item.purchaseOrderId}\n`;
                        message += `   Reason: ${item.reason}\n`;
                    });
                }

                if (errorData.invalidOrders && errorData.invalidOrders.length > 0) {
                    message += `\n${t('receipts.problemOrders')}\n`;
                    errorData.invalidOrders.forEach((order, index) => {
                        message += `\n${index + 1}. Order ID: ${order.purchaseOrderId}\n`;
                        message += `   Name: ${order.orderName}\n`;
                        message += `   Reason: ${order.reason}\n`;
                    });
                }

                showError(message, null, t('receipts.validationError'));
            } else {
                // Standard handling for other errors
                const message = error.response?.data?.message || error.message || t('receipts.errorSendingToMoysklad');
                showError(message, () => sendReceipt(), t('receipts.sendingErrorTitle'));
            }
        } finally {
            setSaving(false);
        }
    };

    // Function to send separate counterparty group
    const handleSendGroup = async (group) => {
        // Filter items only for this group
        const groupItems = receipt.items.filter(item =>
            item.purchaseOrderId === group.purchaseOrderId
        );

        if (groupItems.length === 0) {
            alert(t('receipts.noItemsToSend'));
            return;
        }

        setSaving(true);
        try {
            let receiptId = id || receipt._id;

            // If no ID, create receipt first
            if (!receiptId) {
                const response = await ReceiptService.createReceipt(receipt);
                receiptId = response.data._id;
                setReceipt(prev => ({...prev, _id: receiptId}));
            } else {
                // Update receipt before sending
                await ReceiptService.updateReceipt(receiptId, receipt);
            }

            // Отправляем только конкретную группу
            const response = await ReceiptService.sendSpecificGroup(receiptId, group.purchaseOrderId);
            const result = response.data;

            // Перезагружаем данные приемки для обновления статусов, используя гарантированно доступный receiptId
            await loadReceiptWithId(receiptId);

            // Show results
            let message = `${t('receipts.processingComplete')}\n\n`;
            message += `${t('receipts.counterpartyLabel', {name: group.agentName})}\n`;
            message += `${t('receipts.orderLabel', {name: group.purchaseOrderName})}\n`;
            message += `${t('receipts.itemsLabel', {count: groupItems.length})}\n\n`;

            if (result.success) {
                message += `${t('receipts.successfullySentShort', {id: result.moyskladId})}\n`;
                showSuccess(message);
            } else {
                message += `${t('receipts.errorSendingGroup', {error: result.error})}\n`;
                showError(message, () => handleSendGroup(group), t('receipts.groupSendingError'));
            }

        } catch (error) {
            console.error('Error sending group:', error);
            const message = error.response?.data?.message || error.message || t('receipts.errorSendingGroupMsg');
            showError(message, () => handleSendGroup(group), t('receipts.groupSendingError'));
        } finally {
            setSaving(false);
        }
    };

    // Function to view in MoySklad
    const handleViewInMoysklad = (group) => {
        if (group.result?.moyskladId) {
            window.open(`https://online.moysklad.ru/app/#supply/edit?id=${group.result.moyskladId}`, '_blank');
        }
    };

    if (loading) {
        return <div>{t('msg.loading')}</div>;
    }

    return (
        <Box sx={{ padding: 2 }}>
            <Paper sx={{ padding: 3, marginBottom: 2 }}>
                <h4 style={{ marginBottom: '1rem' }}>
                    {isEdit ? t('receipts.editReceipt') : t('receipts.newReceipt')}
                </h4>

                {/* Basic fields */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 2, marginBottom: 2 }}>
                    <AutocompleteField
                        label={t('receipts.warehouseRequired')}
                        value={receipt.store}
                        onChange={(store) => setReceipt(prev => ({...prev, store}))}
                        searchFunction={ReceiptService.searchStores}
                        placeholder={storesLoading ? t('receipts.loadingWarehouses') : t('receipts.searchWarehouse')}
                        required
                        disabled={receipt.status === 'sent' || storesLoading}
                        minSearchLength={0}
                    />

                    <TextField
                        label={t('receipts.receiptDate')}
                        type="date"
                        value={receipt.receiptDate}
                        onChange={(e) => setReceipt(prev => ({...prev, receiptDate: e.target.value}))}
                        InputLabelProps={{ shrink: true }}
                        disabled={receipt.status === 'sent'}
                        margin="dense"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f24405',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                '&.Mui-focused': {
                                    color: '#f24405'
                                }
                            }
                        }}
                    />
                </Box>

                {/* Display counterparties from orders */}
                {counterparties.length > 0 && (
                    <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <div style={{ fontSize: '14px', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            {t('receipts.counterpartiesInReceipt')}
                        </div>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {counterparties.map((counterparty) => (
                                <Box
                                    key={counterparty.id}
                                    sx={{
                                        padding: '4px 8px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}
                                >
                                    {counterparty.name}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Barcode scanner */}
                {receipt.status === 'draft' && (
                    <BarcodeInput
                        ref={barcodeInputRef}
                        onScan={handleBarcodeScanned}
                        disabled={!scannerActive || !receipt.store || storesLoading || storesLoadError}
                        style={{ marginBottom: 20 }}
                    />
                )}

                {/* Status and info */}

                {storesLoadError && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '12px 16px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffecb3',
                        borderRadius: '4px',
                        color: '#ff6f00',
                        fontSize: '14px'
                    }}>
                        {t('receipts.failedToLoadWarehouses')}
                    </div>
                )}

                {receipt.status === 'sent' && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '12px 16px',
                        backgroundColor: '#e8f5e8',
                        border: '1px solid #c8e6c9',
                        borderRadius: '4px',
                        color: '#2e7d32',
                        fontSize: '14px'
                    }}>
                        {t('receipts.receiptSentToMoysklad')}
                    </div>
                )}

                {(receipt.status === 'error' || (receipt.status === 'draft' && receipt.hasSendError)) && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '12px 16px',
                        backgroundColor: '#ffebee',
                        border: '1px solid #ffcdd2',
                        borderRadius: '4px',
                        color: '#c62828',
                        fontSize: '14px'
                    }}>
                        {t('receipts.sendingError')}
                        {receipt.errorMessage && (
                            <div style={{ marginTop: '4px', fontWeight: 'bold' }}>
                                {receipt.errorMessage}
                            </div>
                        )}
                        {receipt.sendResults && receipt.sendResults.length > 0 && (
                            <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                {t('receipts.lastError')}: {
                                    receipt.sendResults[receipt.sendResults.length - 1].error ||
                                    t('receipts.networkError')
                                }
                            </div>
                        )}
                        {receipt.status === 'draft' && (
                            <div style={{ marginTop: '4px', fontSize: '12px', fontStyle: 'italic' }}>
                                {t('receipts.retryAfterFix')}
                            </div>
                        )}
                    </div>
                )}
            </Paper>

            {/* Items table with counterparty grouping */}
            <Paper sx={{ padding: 2 }}>
                <h6 style={{ marginBottom: '1rem' }}>
                    {t('receipts.itemsInReceipt', {count: receipt.items.length})}
                </h6>

                <GroupedReceiptItems
                    items={receipt.items}
                    sendResults={receipt.sendResults || []}
                    counterparties={counterparties}
                    onQuantityChange={handleQuantityChange}
                    onNoteChange={handleNoteChange}
                    onRemoveItem={handleRemoveItem}
                    onSendGroup={handleSendGroup}
                    onViewInMoysklad={handleViewInMoysklad}
                    disabled={receipt.status === 'sent' || saving}
                    showActions={true}
                    receiptStatus={receipt.status}
                />
            </Paper>

            {/* Control buttons */}
            <Box sx={{ display: 'flex', gap: 2, marginTop: 3, justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    onClick={() => navigate('/receipts')}
                    sx={{
                        backgroundColor: '#757575',
                        '&:hover': { backgroundColor: '#616161' },
                        fontSize: '14px',
                        padding: '10px 20px'
                    }}
                >
                    {t('btn.back')}
                </Button>

                {receipt.status === 'draft' && (
                    <>
                        <Button
                            variant="contained"
                            onClick={() => handleSaveDraft(false)}
                            disabled={saving || !receipt.store || storesLoadError}
                            sx={{
                                backgroundColor: '#4caf50',
                                '&:hover': { backgroundColor: '#45a049' },
                                fontSize: '14px',
                                padding: '10px 20px'
                            }}
                        >
                            {saving ? t('receipts.saving') : t('receipts.saveDraft')}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleSendToMoysklad}
                            disabled={saving || !receipt.store || storesLoadError}
                            sx={{
                                backgroundColor: '#f24405',
                                '&:hover': { backgroundColor: '#a62f04' },
                                fontSize: '14px',
                                padding: '10px 20px'
                            }}
                        >
                            {saving ? t('receipts.sending') : t('receipts.sendToMoysklad')}
                        </Button>
                    </>
                )}
            </Box>

            {/* Quantity input dialog */}
            <Dialog open={quantityDialogOpen} onClose={() => setQuantityDialogOpen(false)}>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    {t('receipts.enterQuantity')}
                </div>
                <DialogContent>
                    {currentScannedItem && (
                        <Box sx={{ marginBottom: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            {currentScannedItem.miniature ? (
                                <img
                                    src={currentScannedItem.miniature}
                                    alt="Product"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        color: '#999'
                                    }}
                                >
                                    {t('msg.noPhoto')}
                                </Box>
                            )}
                            <Box>
                                <div><strong>{currentScannedItem.name}</strong></div>
                                <div style={{ fontSize: '14px', color: '#666' }}>
                                    {t('label.article')}: {currentScannedItem.article}
                                </div>
                                <div style={{ fontSize: '14px', color: '#666' }}>
                                    {t('label.barcode')}: {currentScannedItem.barcode}
                                </div>
                            </Box>
                        </Box>
                    )}
                    <TextField
                        autoFocus
                        label={t('label.quantity')}
                        type="number"
                        value={tempQuantity}
                        onChange={(e) => setTempQuantity(e.target.value)}
                        onKeyPress={handleQuantityDialogKeyPress}
                        inputProps={{ min: 1, step: 1 }}
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f24405',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                '&.Mui-focused': {
                                    color: '#f24405'
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setQuantityDialogOpen(false)}
                        sx={{
                            color: '#757575',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        {t('btn.cancel')}
                    </Button>
                    <Button
                        onClick={handleQuantityConfirmed}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        {t('receipts.add')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог валидации количества */}
            <Dialog open={validationDialog.open} onClose={() => setValidationDialog({open: false, message: '', items: []})}>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    {t('receipts.checkQuantity')}
                </div>
                <DialogContent>
                    <div style={{ marginBottom: '1rem' }}>{validationDialog.message}</div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '0.5rem' }}>
                        {t('receipts.itemsWithoutQuantity')}
                    </div>
                    {validationDialog.items.map((item, index) => (
                        <div key={index} style={{ fontSize: '14px' }}>
                            • {item.name}
                        </div>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationDialog({open: false, message: '', items: []})}>
                        {t('receipts.returnToEditing')}
                    </Button>
                    <Button
                        onClick={handleSetDefaultQuantity}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        {t('receipts.setAllQuantityOne')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог примечания */}
            <Dialog open={noteDialog.open} onClose={handleCloseNoteDialog} maxWidth="sm" fullWidth>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    {t('receipts.itemNote')}
                </div>
                <DialogContent>
                    <div style={{ marginBottom: '1rem' }}>
                        <strong>{noteDialog.itemName}</strong>
                    </div>
                    <TextField
                        autoFocus
                        label={t('receipts.noteOptional')}
                        multiline
                        rows={3}
                        value={tempNote}
                        onChange={(e) => setTempNote(e.target.value)}
                        placeholder={t('receipts.notePlaceholder')}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f24405',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                '&.Mui-focused': {
                                    color: '#f24405'
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseNoteDialog}
                        sx={{
                            color: '#757575',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        {t('btn.cancel')}
                    </Button>
                    {noteDialog.currentNote !== null && (
                        <Button onClick={handleRemoveNote} color="error">
                            {t('receipts.removeMark')}
                        </Button>
                    )}
                    <Button
                        onClick={handleSaveNote}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        {t('btn.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* // === СТАРЫЙ DIALOG: Диалог подтверждения дубликата (ЗАКОММЕНТИРОВАНО) === */}
            {/* Диалог подтверждения дубликата товара */}
            {/*
            <Dialog open={duplicateDialog.open} onClose={handleDuplicateCancel} maxWidth="sm" fullWidth>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    Duplicate Item Detected
                </div>
                <DialogContent>
                    <div style={{ marginBottom: '1rem', fontSize: '16px' }}>
                        Item "<strong>{duplicateDialog.productName}</strong>" has already been added to the receipt.
                    </div>

                    {duplicateDialog.existingOrders && (
                        <div style={{ marginBottom: '1rem', fontSize: '14px', color: '#666' }}>
                            <strong>Already added to orders:</strong><br/>
                            {duplicateDialog.existingOrders}
                        </div>
                    )}

                    <div style={{ marginBottom: '1rem', fontSize: '14px', color: '#666' }}>
                        <strong>Available to add to order:</strong><br/>
                        {duplicateDialog.availableOrder?.purchaseOrderName} ({duplicateDialog.availableOrder?.agentName})
                    </div>

                    <div style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', fontSize: '14px' }}>
                        <strong>Warning!</strong><br/>
                        After clicking 'Confirm' the item will be added again to the specified order.
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDuplicateCancel}
                        sx={{
                            color: '#757575',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDuplicateConfirm}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            */}

            {/* Диалог выбора заказа поставщика */}
            <PurchaseOrderSelector
                open={purchaseOrderSelectorOpen}
                orders={availableOrders}
                allOrders={currentProductForSelection?.allOrders}
                selectedOrderIds={currentProductForSelection?.selectedOrderIds || []}
                productName={currentProductForSelection?.name || ''}
                productBarcode={currentProductForSelection?.barcode || ''}
                productImage={currentProductForSelection?.miniature || ''}
                onClose={() => {
                    setPurchaseOrderSelectorOpen(false);
                    setCurrentProductForSelection(null);
                    setAvailableOrders([]);
                }}
                onSelect={handlePurchaseOrderSelected}
            />

            {/* Модальное окно уведомлений */}
            <NotificationModal
                open={notification.open}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                onRetry={notification.onRetry}
                onClose={closeNotification}
            />
        </Box>
    );
}